import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DependencyService {

  constructor() { }

  updateVisibility(attributes: any[], options: any[], selectedValues: { [key: number]: number }, selectedOptionValues: { [key: number]: number }, attributeOptions?: any[]) {
    let hasChanges = false;
    
    // Update visibility for attributes and their values
    attributes.forEach(attribute => {
      // Store previous state to detect changes
      const wasHidden = attribute.isHidden;
      const wasDisabled = !attribute.isEnable;

      // Update visibility based on dependencies
      attribute.isHidden = this.checkVisibility(attribute.dependencies, selectedValues, selectedOptionValues);
      
      // If visibility has changed to hidden or the attribute was already hidden but has a value
      if ((attribute.isHidden && !wasHidden) || 
          (attribute.isHidden && selectedValues[attribute.attributeOptionId] !== undefined)) {
        
        // Remove from selectedValues
        if (selectedValues[attribute.attributeOptionId] !== undefined) {
          delete selectedValues[attribute.attributeOptionId];
        }
        
        // Reset all values
        attribute.values.forEach((value: any) => {
          if (value.selectedValue) {
            value.selectedValue = false;
          }
        });
        
        // Reset text fields if applicable
        if (attribute.componentType === 'Text') {
          attribute.selectedValue = '';
        }
        
        // Remove from attributeOptions array if provided
        if (attributeOptions) {
          const idx = attributeOptions.findIndex(opt => opt.attributeOptionId === attribute.attributeOptionId);
          if (idx > -1) {
            attributeOptions.splice(idx, 1);
          }
        }
        
        hasChanges = true;
      }
      
      // We also need to check if values within the attribute should be hidden/shown
      if (!attribute.isHidden) {
        attribute.values.forEach((value: any) => {
          // Implement logic for value visibility if needed
          if (value.dependencies && value.dependencies.length > 0) {
            value.isVisible = !this.checkVisibility(value.dependencies, selectedValues, selectedOptionValues);
            
            // If value becomes invisible and was selected, reset it
            if (!value.isVisible && value.selectedValue) {
              value.selectedValue = false;
              
              // Also update selectedValues if this was the selected value
              if (selectedValues[attribute.attributeOptionId] === value.valueId) {
                delete selectedValues[attribute.attributeOptionId];
                
                // Remove from attributeOptions array if provided
                if (attributeOptions) {
                  const idx = attributeOptions.findIndex(opt => opt.attributeOptionId === attribute.attributeOptionId);
                  if (idx > -1) {
                    attributeOptions.splice(idx, 1);
                  }
                }
                
                hasChanges = true;
              }
            }
          }
        });
      }
    });

    // Update visibility for options
    options.forEach(option => {
      // Store previous state to detect changes
      const wasHidden = option.isHidden;
      const wasDisabled = !option.isEnable;
      
      // Update visibility based on dependencies
      option.isHidden = this.checkVisibility(option.dependencies, selectedValues, selectedOptionValues);
      
      // If visibility has changed to hidden or the option was already hidden but has a value
      if ((option.isHidden && !wasHidden) || 
          (option.isHidden && selectedOptionValues[option.attributeOptionId] !== undefined)) {
        
        // Remove from selectedOptionValues
        if (selectedOptionValues[option.attributeOptionId] !== undefined) {
          delete selectedOptionValues[option.attributeOptionId];
        }
        
        // Reset all values
        option.values.forEach((value: any) => {
          if (value.selectedValue) {
            value.selectedValue = false;
          }
        });
        
        // Reset text fields if applicable
        if (option.componentType === 'Text') {
          option.selectedValue = '';
        }
        
        // Remove from attributeOptions array if provided
        if (attributeOptions) {
          const idx = attributeOptions.findIndex(opt => opt.attributeOptionId === option.attributeOptionId);
          if (idx > -1) {
            attributeOptions.splice(idx, 1);
          }
        }
        
        hasChanges = true;
      }
      
      // We also need to check if values within the option should be hidden/shown
      if (!option.isHidden) {
        option.values.forEach((value: any) => {
          // Implement logic for value visibility if needed
          if (value.dependencies && value.dependencies.length > 0) {
            value.isVisible = !this.checkVisibility(value.dependencies, selectedValues, selectedOptionValues);
            
            // If value becomes invisible and was selected, reset it
            if (!value.isVisible && value.selectedValue) {
              value.selectedValue = false;
              
              // Also update selectedOptionValues if this was the selected value
              if (selectedOptionValues[option.attributeOptionId] === value.valueId) {
                delete selectedOptionValues[option.attributeOptionId];
                
                // Remove from attributeOptions array if provided
                if (attributeOptions) {
                  const idx = attributeOptions.findIndex(opt => opt.attributeOptionId === option.attributeOptionId);
                  if (idx > -1) {
                    attributeOptions.splice(idx, 1);
                  }
                }
                
                hasChanges = true;
              }
            }
          }
        });
      }
    });

    // Process cascading dependencies by recursively checking affected attributes/options
    if (hasChanges) {
      this.processCascadingDependencies(attributes, options, selectedValues, selectedOptionValues, attributeOptions);
    }
  }

  private processCascadingDependencies(attributes: any[], options: any[], selectedValues: { [key: number]: number }, selectedOptionValues: { [key: number]: number }, attributeOptions?: any[]) {
    let hasChanges = false;

    // Check for attributes that need to be reset due to dependency changes
    attributes.forEach(attribute => {
      if (!attribute.isHidden) {
        const shouldBeHidden = this.checkVisibility(attribute.dependencies, selectedValues, selectedOptionValues);
        if (shouldBeHidden !== attribute.isHidden) {
          attribute.isHidden = shouldBeHidden;
          if (attribute.isHidden && selectedValues[attribute.attributeOptionId] !== undefined) {
            delete selectedValues[attribute.attributeOptionId];
            attribute.values.forEach((value: any) => {
              if (value.selectedValue) {
                value.selectedValue = false;
              }
            });
            if (attribute.componentType === 'Text') {
              attribute.selectedValue = '';
            }
            
            // Remove from attributeOptions array if provided
            if (attributeOptions) {
              const idx = attributeOptions.findIndex(opt => opt.attributeOptionId === attribute.attributeOptionId);
              if (idx > -1) {
                attributeOptions.splice(idx, 1);
              }
            }
            
            hasChanges = true;
          }
        }
      }
    });

    // Check for options that need to be reset due to dependency changes
    options.forEach(option => {
      if (!option.isHidden) {
        const shouldBeHidden = this.checkVisibility(option.dependencies, selectedValues, selectedOptionValues);
        if (shouldBeHidden !== option.isHidden) {
          option.isHidden = shouldBeHidden;
          if (option.isHidden && selectedOptionValues[option.attributeOptionId] !== undefined) {
            delete selectedOptionValues[option.attributeOptionId];
            option.values.forEach((value: any) => {
              if (value.selectedValue) {
                value.selectedValue = false;
              }
            });
            if (option.componentType === 'Text') {
              option.selectedValue = '';
            }
            
            // Remove from attributeOptions array if provided
            if (attributeOptions) {
              const idx = attributeOptions.findIndex(opt => opt.attributeOptionId === option.attributeOptionId);
              if (idx > -1) {
                attributeOptions.splice(idx, 1);
              }
            }
            
            hasChanges = true;
          }
        }
      }
    });

    // If any changes were made, process dependencies again to handle cascading effects
    if (hasChanges) {
      this.processCascadingDependencies(attributes, options, selectedValues, selectedOptionValues, attributeOptions);
    }
  }

  private checkVisibility(dependencies: any[], selectedValues: { [key: number]: number }, selectedOptionValues: { [key: number]: number }): boolean {
    if (!dependencies || dependencies.length === 0) {
      return false;
    }

    return dependencies.every(dep => {
      if (dep.objectType === 'AttributeValue') {
        return selectedValues[dep.attributeId] !== dep.valueId;
      }
      else if (dep.objectType === 'Attribute') {
        if (selectedValues[dep.attributeId] === -1 || selectedValues[dep.attributeId] === undefined) {
          return true;
        }

        return selectedValues[dep.attributeId] === dep.valueId;
      }
      else if (dep.objectType === 'OptionValue') {
        return selectedOptionValues[dep.attributeId] !== dep.valueId;
      }
      
      return true;
    });
  }

  // Reset all dependencies downstream from a changed component
  resetCascadingDependencies(changedAttributeId: number, attributes: any[], options: any[], 
                            selectedValues: { [key: number]: number }, selectedOptionValues: { [key: number]: number },
                            attributeOptions: any[]) {
    // Find attributes that depend on the changed attribute
    const resetAttributeIds: number[] = [];
    
    // Find attributes that depend on the changed attribute
    attributes.forEach(attribute => {
      if (this.isDependentOn(attribute.dependencies, changedAttributeId)) {
        resetAttributeIds.push(attribute.attributeOptionId);
        
        // Reset the attribute's selection
        if (selectedValues[attribute.attributeOptionId] !== undefined) {
          delete selectedValues[attribute.attributeOptionId];
        }
        
        // Reset all values to unselected
        attribute.values.forEach((value: any) => {
          if (value.selectedValue) {
            value.selectedValue = false;
          }
        });
        
        // Reset text fields if applicable
        if (attribute.componentType === 'Text') {
          attribute.selectedValue = '';
        }
        
        // Remove from attributeOptions array
        const idx = attributeOptions.findIndex(opt => opt.attributeOptionId === attribute.attributeOptionId);
        if (idx > -1) {
          attributeOptions.splice(idx, 1);
        }
      }
    });
    
    // Find options that depend on the changed attribute
    options.forEach(option => {
      if (this.isDependentOn(option.dependencies, changedAttributeId)) {
        // Reset the option's selection
        if (selectedOptionValues[option.attributeOptionId] !== undefined) {
          delete selectedOptionValues[option.attributeOptionId];
        }
        
        // Reset all values to unselected
        option.values.forEach((value: any) => {
          if (value.selectedValue) {
            value.selectedValue = false;
          }
        });
        
        // Reset text fields if applicable
        if (option.componentType === 'Text') {
          option.selectedValue = '';
        }
        
        // Remove from attributeOptions array
        const idx = attributeOptions.findIndex(opt => opt.attributeOptionId === option.attributeOptionId);
        if (idx > -1) {
          attributeOptions.splice(idx, 1);
        }
      }
    });
    
    // Recursively reset dependencies of reset attributes
    resetAttributeIds.forEach(attrId => {
      this.resetCascadingDependencies(attrId, attributes, options, selectedValues, selectedOptionValues, attributeOptions);
    });
    
    // Update the visibility after all resets
    this.updateVisibility(attributes, options, selectedValues, selectedOptionValues, attributeOptions);
    
    // Faça uma verificação final para remover quaisquer dependências inválidas remanescentes
    this.finalCleanup(attributes, options, selectedValues, selectedOptionValues, attributeOptions);
  }
  
  // Método para fazer uma verificação final de limpeza
  private finalCleanup(attributes: any[], options: any[], selectedValues: { [key: number]: number }, 
                      selectedOptionValues: { [key: number]: number }, attributeOptions: any[]) {
    if (!attributeOptions) return;
    
    // Cria uma lista dos IDs que devem ser mantidos
    const validAttributeIds = new Set();
    const validOptionIds = new Set();
    
    // Adiciona IDs de atributos válidos (visíveis e habilitados com valores selecionados)
    attributes.forEach(attr => {
      if (!attr.isHidden && attr.isEnable && selectedValues[attr.attributeOptionId] !== undefined) {
        validAttributeIds.add(attr.attributeOptionId);
      }
    });
    
    // Adiciona IDs de opções válidas (visíveis e habilitadas com valores selecionados)
    options.forEach(opt => {
      if (!opt.isHidden && opt.isEnable && selectedOptionValues[opt.attributeOptionId] !== undefined) {
        validOptionIds.add(opt.attributeOptionId);
      }
    });
    
    // Filtra attributeOptions para manter apenas itens válidos
    const validOptions = attributeOptions.filter(option => {
      // Verifica se o ID está na lista de IDs válidos
      const isValidAttribute = validAttributeIds.has(option.attributeOptionId);
      const isValidOption = validOptionIds.has(option.attributeOptionId);
      
      // Se é um atributo válido, verifica se o valueId corresponde ao valor selecionado
      if (isValidAttribute) {
        return selectedValues[option.attributeOptionId] === option.valueId;
      }
      
      // Se é uma opção válida, verifica se o valueId corresponde ao valor selecionado
      if (isValidOption) {
        return selectedOptionValues[option.attributeOptionId] === option.valueId;
      }
      
      // Se não é nem atributo nem opção válida, remove
      return false;
    });
    
    // Substitui a lista original
    while (attributeOptions.length > 0) {
      attributeOptions.pop();
    }
    
    // Adiciona os itens válidos de volta
    validOptions.forEach(option => {
      attributeOptions.push(option);
    });
  }
  
  private isDependentOn(dependencies: any[], attributeId: number): boolean {
    if (!dependencies || dependencies.length === 0) {
      return false;
    }
    
    return dependencies.some(dep => 
      (dep.objectType === 'AttributeValue' || dep.objectType === 'Attribute') && 
      dep.attributeId === attributeId
    );
  }
}